import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrgNavigation, extractBasePath } from '../../utils/navigation';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  open, 
  onClose, 
  variant = 'permanent',
  onCollapseChange
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateToOrgPath } = useOrgNavigation();

  const drawerWidth = collapsed ? 64 : 240;

  const menuSections = [
    {
      title: 'Overview',
      items: [
        { 
          text: 'Map', 
          icon: <img src={process.env.PUBLIC_URL + '/Map.svg'} alt="Map" style={{ width: '24px', height: '24px' }} />, 
          path: '/map'
        },
        { 
          text: 'My Projects', 
          icon: <img src={process.env.PUBLIC_URL + '/Project.svg'} alt="Projects" style={{ width: '24px', height: '24px' }} />, 
          path: '/projects'
        },
      ]
    },
    {
      title: 'Assets',
      items: [
        { 
          text: 'Chargers', 
          icon: <img src={process.env.PUBLIC_URL + '/Chargers.svg'} alt="Chargers" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />, 
          path: '/assets/chargers'
        },
        { 
          text: 'Excavators', 
          icon: <img src={process.env.PUBLIC_URL + '/DiggerIcon.svg'} alt="Excavators" style={{ width: '24px', height: '24px' , filter: 'brightness(0) invert(1)' }} />, 
          path: '/assets/excavators'
        },
        { 
          text: 'Switchboards', 
          icon: <img src={process.env.PUBLIC_URL + '/Switchboards.svg'} alt="Switchboards" style={{ width: '24px', height: '24px' }} />, 
          path: '/assets/switchboards'
        },
        { 
          text: 'Energy Meters', 
          icon: <img src={process.env.PUBLIC_URL + '/Energy Meters.svg'} alt="Energy Meters" style={{ width: '24px', height: '24px' }} />, 
          path: '/assets/energy-meters'
        },

      ]
    },
/*     {
      title: 'Integrations',
      items: [
        { 
          text: 'My Integrations', 
          icon: <img src={process.env.PUBLIC_URL + '/icon.svg'} alt="Integrations" style={{ width: '24px', height: '24px' }} />, 
          path: '/integrations'
        },
        { 
          text: 'Data Integrations', 
          icon: <img src={process.env.PUBLIC_URL + '/build.svg'} alt="Data Integrations" style={{ width: '24px', height: '24px' }} />, 
          path: '/integrations/data'
        },
      ]
    }, */
    {
      title: 'Administration',
      items: [
        { 
          text: 'Users', 
          icon: <PersonIcon />, 
          path: '/admin/users'
        },
        { 
          text: 'Companies', 
          icon: <BusinessIcon />, 
          path: '/admin/companies'
        },
        { 
          text: 'Reports', 
          icon: <AssessmentIcon />, 
          path: 'https://www.insight.aneobuild.com',
          external: true
        },
/*          { 
          text: 'Notifications', 
          icon: <NotificationsIcon />, 
          path: '/admin/notifications'
        }, */
      ]
    },
  ];

  const handleNavigation = (path: string, external: boolean = false) => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      const orgPath = navigateToOrgPath(path);
      navigate(orgPath);
      if (isMobile) {
        onClose();
      }
    }
  };

  const handleToggleCollapse = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Logo */}
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: collapsed ? 'center' : 'space-between',
        padding: '16px',
        backgroundColor: (theme) => theme.palette.primary.main,
        //minHeight: '64px !important'
        height: '64px'
      }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <svg width="180" height="180" viewBox="0 0 180 180" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="180" height="180" fill={theme.palette.primary.light}/>
                <path d="M78.2323 25.7096L44 167.96L65.9187 155.307L74.1218 117.847L109.505 97.4196L118.413 125L136 114.846L101.994 12L78.2442 25.7156L78.2323 25.7096ZM79.2942 94.21L91.0828 40.3738L103.88 80.0111L79.2942 94.21Z" fill="#FFFFFF"/>
              </svg>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.extra.neutrals.white, 
                fontWeight: 500,
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1
              }}
            >
              powersite
            </Typography>
          </Box>
        )}
        <IconButton 
          onClick={handleToggleCollapse} 
          size="small"
          sx={{ color: theme.palette.extra.neutrals.white }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>

      {/* Navigation Menu */}
      <Box sx={{ 
        flexGrow: 1, 
        backgroundColor: (theme) => theme.palette.primary.dark,
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        //justifyContent: 'space-between'
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: (theme) => theme.palette.primary.dark,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => theme.palette.primary.main,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.light,
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: (theme) => `${theme.palette.primary.main} ${theme.palette.primary.dark}`,
      }}>
        <Box sx={{ flexGrow: 1 }}>
          {menuSections.map((section) => (
            <Box key={section.title}>
              {/* Section Title */}
              {!collapsed && (
                <Box sx={{ px: 3, py: 2 }}>
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.extra.neutrals.white,
                      fontSize: '17px !important',
                      fontWeight: 500,
                      letterSpacing: '0.1px',
                      fontFamily: 'Montserrat',
                      lineHeight: '157%',
                      fontStyle: 'normal',
                      margin: 0,
                      padding: 0
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
              )}
              
              {/* Section Items */}
              <List sx={{ py: 0 }}>
                {section.items.map((item) => {
                  const basePath = extractBasePath(location.pathname);
                  const isActive = basePath === item.path || basePath.startsWith(item.path + '/');
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0 }}>
                                              <ListItemButton
                          onClick={() => handleNavigation(item.path, item.external)}
                          sx={{
                            display: 'flex',
                            //padding: '4px 16px',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            //width: 'calc(100% - 32px)',
                            width: '100%',
                            height: 40,
                            //borderRadius: 1,
                            backgroundColor: isActive ? (theme) => theme.palette.primary.light : 'transparent',
                            color: theme.palette.extra.neutrals.white,
                            '&:hover': {
                              backgroundColor: isActive ? (theme) => theme.palette.primary.light : 'rgba(255, 255, 255, 0.05)',
                            },
                            justifyContent: collapsed ? 'center' : 'flex-start'
                            //mx: 2,
                          }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: collapsed ? 0 : 48,
                            color: theme.palette.extra.neutrals.white,
                            fontSize: '24px',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            <ListItemText 
                              primary={item.text}
                              primaryTypographyProps={{
                                fontSize: '17px !important',
                                fontWeight: isActive ? 500 : 400,
                                lineHeight: '22px !important',
                                color: 'inherit',
                                fontStyle: 'normal',
                                letterSpacing: '0em',
                                margin: 0,
                                padding: 0
                              }}
                            />
                            {item.external && (
                              <img 
                                src={process.env.PUBLIC_URL + '/NewWindowIcon.svg'} 
                                alt="New Window" 
                                style={{ 
                                  width: '16px', 
                                  height: '16px',
                                  marginLeft: '8px',
                                  filter: 'brightness(0) invert(1)'
                                }} 
                              />
                            )}
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
        
        {/* Company Homepage Link */}
        <Box sx={{ 
          pb: collapsed ? 1 : 2, 
          pt: collapsed ? 1 : 2, 
          borderTop: collapsed ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: (theme) => theme.palette.primary.dark
        }}>

          <ListItem disablePadding>
            {!collapsed && (
              <ListItemButton
                component="a"
                href="https://www.aneobuild.com/no/kontakt-oss"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: 32,
                  backgroundColor: 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                  justifyContent: 'flex-start',
                  textDecoration: 'none'
                }}
              >
                <ListItemText 
                  primary="Kontakt oss"
                  primaryTypographyProps={{
                    fontSize: '12px !important',
                    fontWeight: 300,
                    lineHeight: '22px !important',
                    color: 'inherit',
                    fontStyle: 'normal',
                    letterSpacing: '0em',
                    margin: 0,
                    padding: 0
                  }}
                />
                <img 
                  src={process.env.PUBLIC_URL + '/NewWindowIcon.svg'} 
                  alt="New Window" 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    marginLeft: '4px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
              </ListItemButton>
            )}
          </ListItem>
          <ListItem disablePadding>
            {!collapsed && (
              <ListItemButton
                component="a"
                href="https://www.aneobuild.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: 32,
                  backgroundColor: 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                  justifyContent: 'flex-start',
                  textDecoration: 'none'
                }}
              >
                <ListItemText 
                  primary="Aneo Build"
                  primaryTypographyProps={{
                    fontSize: '12px !important',
                    fontWeight: 300,
                    lineHeight: '22px !important',
                    color: 'inherit',
                    fontStyle: 'normal',
                    letterSpacing: '0em',
                    margin: 0,
                    padding: 0
                  }}
                />
                <img 
                  src={process.env.PUBLIC_URL + '/NewWindowIcon.svg'} 
                  alt="New Window" 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    marginLeft: '4px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
              </ListItemButton>
            )}
          </ListItem>
        </Box>
      </Box>

    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          backgroundColor: 'transparent',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
